import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    PiArrowDownLeftBold,
    PiArrowLeftBold,
    PiArrowUpRightBold,
    PiFilePdfBold,
    PiGearSixBold,
    PiHandCoinsBold,
    PiImageBold,
    PiPercentBold,
    PiPlusMinusBold,
    PiTrashBold,
    PiWalletDuotone,
} from 'react-icons/pi';
import { Badge, Button, LoadingState, PageHeader } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import ConfirmModal from '@v2/components/ui/ConfirmModal';
import api from '@v2/lib/api';
import { downloadPdf } from '@v2/lib/downloadPdf';
import { cn } from '@v2/utils/cn';
import { useI18n } from '@v2/providers/I18nProvider';

type EarningRow = {
    deal_id: number;
    deal_number?: string | null;
    deal_title?: string | null;
    installment_label: string;
    due_date?: string | null;
    paid_at?: string | null;
    base: number;
    rate_pct: number;
    amount: number;
    competence?: string | null;
    original_competence?: string | null;
    is_retroactive?: boolean;
    category?: 'normal' | 'retroactive' | 'defaulted' | 'forfeited' | 'clawback';
    status: 'earned' | 'forecast' | 'defaulted' | 'forfeited' | 'cancelled' | 'clawback';
};

type MonthReport = {
    month: string;
    items: EarningRow[];
    defaulted_items: EarningRow[];
    forfeited_items: EarningRow[];
    clawback_items: EarningRow[];
    normal_total: number;
    retroactive_total: number;
    gross_total: number;
    defaulted_total: number;
    forfeited_total: number;
    clawback_total: number;
    net_total: number;
};

type WalletEntry = {
    id: number;
    type: 'payout' | 'adjustment';
    amount: number;
    occurred_on?: string | null;
    method?: string | null;
    reference?: string | null;
    receipt_url?: string | null;
    notes?: string | null;
    created_by?: string | null;
};

type Wallet = {
    current_month: string;
    earned_current_month: number;
    earned_total: number;
    forecast_total: number;
    paid_out_total: number;
    adjustments_total: number;
    balance: number;
    months: Array<{ month: string; earned: number; available: boolean; cumulative: number; deals: number }>;
    entries: WalletEntry[];
};

type Beneficiary = {
    id: number;
    name: string;
    email?: string | null;
    avatar_url?: string | null;
    active: boolean;
    payout_notes?: string | null;
    current_rate_pct: number;
    user?: { id: number; name: string; email?: string | null } | null;
    rates: Array<{ rate_pct: number; effective_from: string; notes?: string | null }>;
    wallet: Wallet;
};

type RatePreview = {
    rate_pct: number;
    effective_from: string;
    current_rate_pct: number;
    affected_deals: number;
    earned_delta: number;
    total_delta: number;
    earned_before: number;
    earned_after: number;
};

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 }).format(v ?? 0);

const fmtDate = (iso?: string | null) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('pt-BR') : '—');

function fmtMonth(ym: string) {
    const [y, m] = ym.split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, 1).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
}

function fmtMonthShort(ym: string) {
    const [y, m] = ym.split('-').map(Number);
    const name = new Date(y, (m ?? 1) - 1, 1).toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
    return `${name.charAt(0).toUpperCase()}${name.slice(1)}/${String(y).slice(2)}`;
}

function fmtMonthLong(ym: string) {
    const [y, m] = ym.split('-').map(Number);
    const name = new Date(y, (m ?? 1) - 1, 1).toLocaleDateString('pt-BR', { month: 'long' });
    return `${name.charAt(0).toUpperCase()}${name.slice(1)}/${y}`;
}

function apiMessage(err: unknown): string | null {
    const data = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })?.response?.data;
    return Object.values(data?.errors ?? {})[0]?.[0] ?? data?.message ?? null;
}

type StatementRow = {
    key: string;
    date: string;
    title: string;
    detail?: string;
    amount: number;
    balance: number;
    month?: string;
    entry?: WalletEntry;
};

/** Extrato bancário: créditos mensais (meses disponíveis) + transferências/ajustes, com saldo corrente. */
function buildStatement(wallet: Wallet, t: (key: string, replace?: Record<string, string | number>) => string): StatementRow[] {
    const rows: Omit<StatementRow, 'balance'>[] = [
        ...wallet.months
            .filter((m) => m.available && Math.abs(m.earned) > 0.009)
            .map((m) => ({
                key: `credit-${m.month}`,
                date: `${m.month}-01`,
                title: t('v2.gluteleader_wallet.credit_desc', { month: fmtMonthLong(m.month) }),
                detail: `${m.deals} venda(s)`,
                amount: m.earned,
                month: m.month,
            })),
        ...wallet.entries.map((e) => ({
            key: `entry-${e.id}`,
            date: e.occurred_on ?? '',
            title: e.type === 'payout'
                ? `${t('v2.gluteleader_wallet.payout_desc')}${e.method ? ` ${e.method.toUpperCase()}` : ''}`
                : t('v2.gluteleader_wallet.adjustment_desc'),
            detail: [e.reference, e.notes, e.created_by].filter(Boolean).join(' · ') || undefined,
            amount: e.amount,
            entry: e,
        })),
    ].sort((a, b) => (a.date === b.date ? a.key.localeCompare(b.key) : a.date.localeCompare(b.date)));

    let balance = 0;
    const withBalance = rows.map((row) => {
        balance += row.amount;
        return { ...row, balance: Math.round(balance * 100) / 100 };
    });

    return withBalance.reverse();
}

const STATUS_COLOR: Record<EarningRow['status'], 'success' | 'info' | 'danger' | 'warning' | 'secondary'> = {
    earned: 'success',
    forecast: 'info',
    defaulted: 'danger',
    forfeited: 'danger',
    cancelled: 'secondary',
    clawback: 'warning',
};

function Kpi({ label, value, tone = 'gray' }: { label: string; value: string; tone?: 'gray' | 'emerald' | 'sky' | 'amber' | 'violet' }) {
    const tones = {
        gray: 'border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900',
        emerald: 'border-emerald-200 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-950/30',
        sky: 'border-sky-200 bg-sky-50 dark:border-sky-900/50 dark:bg-sky-950/30',
        amber: 'border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30',
        violet: 'border-violet-200 bg-violet-50 dark:border-violet-900/50 dark:bg-violet-950/30',
    };

    return (
        <div className={cn('rounded-xl border p-4', tones[tone])}>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">{label}</p>
            <p className="mt-1 text-xl font-black tabular-nums text-gray-900 dark:text-zinc-50">{value}</p>
        </div>
    );
}

export default function GluteleaderBeneficiaryPage({ self = false }: { self?: boolean }) {
    const { t } = useI18n();
    const qc = useQueryClient();
    const { beneficiaryId } = useParams<{ beneficiaryId: string }>();
    const endpoint = self ? '/commissioning/gluteleader/me' : `/commissioning/gluteleader/beneficiaries/${beneficiaryId}`;
    const queryKey = ['gluteleader', 'beneficiary', self ? 'me' : beneficiaryId];

    const query = useQuery({
        queryKey,
        queryFn: () => api.get(endpoint).then((r) => r.data as { data: Beneficiary; can_manage: boolean }),
        retry: false,
    });

    const beneficiary = query.data?.data;
    const canManage = query.data?.can_manage === true && !self;
    const wallet = beneficiary?.wallet;

    const [month, setMonth] = useState<string | null>(null);
    useEffect(() => {
        if (!month && wallet) {
            const available = wallet.months.filter((m) => m.available);
            setMonth(available.at(-1)?.month ?? wallet.months.at(-1)?.month ?? wallet.current_month);
        }
    }, [wallet, month]);

    const monthQuery = useQuery({
        queryKey: [...queryKey, 'month', month],
        queryFn: () => api.get(`/commissioning/gluteleader/beneficiaries/${beneficiary?.id}/months/${month}`)
            .then((r) => r.data.data as MonthReport),
        enabled: !!beneficiary && !!month,
    });

    const [modal, setModal] = useState<'payout' | 'adjustment' | 'rate' | 'config' | null>(null);
    const [tab, setTab] = useState<'statement' | 'detail'>('statement');
    const [downloading, setDownloading] = useState(false);
    const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
    const [showAllMonths, setShowAllMonths] = useState(false);
    const [deleteEntry, setDeleteEntry] = useState<WalletEntry | null>(null);
    const refresh = () => {
        void qc.invalidateQueries({ queryKey: ['gluteleader'] });
    };

    const removeEntry = useMutation({
        mutationFn: (id: number) => api.delete(`/commissioning/gluteleader/entries/${id}`),
        onSuccess: () => {
            toast.success(t('v2.gluteleader_wallet.entry_deleted'));
            setDeleteEntry(null);
            refresh();
        },
    });

    const maxEarned = useMemo(() => Math.max(1, ...(wallet?.months ?? []).map((m) => Math.abs(m.earned))), [wallet]);

    if (query.isLoading) return <LoadingState variant="page" />;
    if (!beneficiary || !wallet) {
        return <p className="text-sm text-gray-500">{t('v2.gluteleader_wallet.empty_month')}</p>;
    }

    const statementRows = buildStatement(wallet, t);
    // O mês escolhido vale para indicadores, extrato e holerite (o saldo do cartão é sempre o de hoje).
    const selected = month ?? wallet.current_month;
    const selectedLabel = fmtMonthLong(selected);
    const monthRow = wallet.months.find((m) => m.month === selected);
    const cumulativeUntil = [...wallet.months].filter((m) => m.month <= selected).at(-1)?.cumulative ?? 0;
    const transferredInMonth = Math.abs(wallet.entries
        .filter((e) => e.type === 'payout' && (e.occurred_on ?? '').startsWith(selected))
        .reduce((acc, e) => acc + e.amount, 0));
    const balanceAtMonthEnd = statementRows.find((r) => r.date.slice(0, 7) <= selected)?.balance ?? 0;
    const visibleRows = showAllMonths ? statementRows : statementRows.filter((r) => r.date.startsWith(selected));
    const downloadStatement = async (target: string | null = month) => {
        if (!target) return;
        setDownloading(true);
        try {
            await downloadPdf(`/commissioning/gluteleader/beneficiaries/${beneficiary.id}/months/${target}/statement`, `holerite-gl-${target}.pdf`);
        } catch {
            toast.error(t('v2.gluteleader_wallet.statement_failed'));
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="space-y-6">
            {!self && (
                <Link to="/commissioning/gluteleader" className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800">
                    <PiArrowLeftBold className="size-3.5" aria-hidden /> {t('v2.gluteleader_wallet.back')}
                </Link>
            )}

            <PageHeader title={self ? t('v2.gluteleader_wallet.my_title') : t('v2.gluteleader_wallet.account_title')}>
                {canManage && (
                    <div className="flex flex-wrap gap-2">
                        <Button type="button" size="sm" onClick={() => setModal('payout')}>
                            <PiHandCoinsBold className="mr-1 size-4" aria-hidden /> {t('v2.gluteleader_wallet.register_payout')}
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => setModal('adjustment')}>
                            <PiPlusMinusBold className="mr-1 size-4" aria-hidden /> {t('v2.gluteleader_wallet.register_adjustment')}
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => setModal('rate')}>
                            <PiPercentBold className="mr-1 size-4" aria-hidden /> {t('v2.gluteleader_wallet.change_rate')}
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => setModal('config')}>
                            <PiGearSixBold className="mr-1 size-4" aria-hidden /> {t('v2.gluteleader_wallet.configure')}
                        </Button>
                    </div>
                )}
            </PageHeader>

            {/* Cartão da conta */}
            <section className="relative overflow-hidden rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-950 to-zinc-800 p-6 text-white shadow-lg">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-primary" aria-hidden />
                <PiWalletDuotone className="pointer-events-none absolute -bottom-8 -right-6 size-44 text-primary/10" aria-hidden />
                <div className="relative flex flex-wrap items-start justify-between gap-6">
                    <div className="flex items-center gap-3">
                        <span className="flex size-12 items-center justify-center rounded-xl bg-primary/15 text-primary">
                            <PiWalletDuotone className="size-7" aria-hidden />
                        </span>
                        <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60">{t('v2.gluteleader_wallet.account_title')}</p>
                            <p className="text-lg font-bold">{beneficiary.name}</p>
                            <p className="text-xs text-white/70">
                                {t('v2.gluteleader_wallet.rate')} {beneficiary.current_rate_pct.toLocaleString('pt-BR')}%
                                {' · '}
                                {beneficiary.user
                                    ? t('v2.gluteleader_wallet.linked_user', { name: beneficiary.user.name })
                                    : t('v2.gluteleader_wallet.no_user')}
                                {!beneficiary.active && ' · inativo'}
                            </p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60">{t('v2.gluteleader_wallet.balance')}</p>
                        <p className="text-4xl font-black tabular-nums text-primary">{brl(wallet.balance)}</p>
                        <p className="text-xs text-white/70">{t('v2.gluteleader_wallet.available_now')}</p>
                        <p className="mt-1 text-xs text-white/70">
                            {t('v2.gluteleader_wallet.forecast')}: <span className="font-semibold text-white">{brl(wallet.forecast_total)}</span>
                        </p>
                    </div>
                </div>
                {beneficiary.payout_notes && (
                    <p className="relative mt-4 rounded-lg bg-white/10 px-3 py-2 text-xs text-white/80">{beneficiary.payout_notes}</p>
                )}
            </section>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Kpi label={t('v2.gluteleader_wallet.kpi_month_in', { month: selectedLabel })} value={brl(monthRow?.earned ?? 0)} tone="emerald" />
                <Kpi label={t('v2.gluteleader_wallet.kpi_cumulative', { month: selectedLabel })} value={brl(cumulativeUntil)} />
                <Kpi label={t('v2.gluteleader_wallet.kpi_transferred', { month: selectedLabel })} value={brl(transferredInMonth)} tone="violet" />
                <Kpi label={t('v2.gluteleader_wallet.kpi_balance_end', { month: selectedLabel })} value={brl(balanceAtMonthEnd)} tone="amber" />
            </div>

            <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
                <div className="mb-3 flex items-center justify-between">
                    <p className="text-sm font-bold text-gray-900 dark:text-zinc-50">{t('v2.gluteleader_wallet.tab_months')}</p>
                    <p className="text-[11px] text-gray-500">
                        <span className="mr-1 inline-block size-2 rounded-sm bg-primary" />{t('v2.gluteleader_wallet.status_earned')}
                        <span className="ml-3 mr-1 inline-block size-2 rounded-sm bg-gray-300 dark:bg-zinc-600" />{t('v2.gluteleader_wallet.status_forecast')}
                    </p>
                </div>
                {wallet.months.length === 0 ? (
                    <p className="text-xs text-gray-500">{t('v2.gluteleader_wallet.empty_month')}</p>
                ) : (
                    <div className="flex gap-2 overflow-x-auto pb-2">
                        {wallet.months.map((m) => (
                            <button
                                key={m.month}
                                type="button"
                                onClick={() => setMonth(m.month)}
                                className={cn(
                                    'flex w-24 shrink-0 flex-col items-center rounded-lg border px-2 py-2 text-[11px] transition-colors',
                                    selected === m.month
                                        ? 'border-primary bg-primary/10 ring-1 ring-primary'
                                        : 'border-gray-100 hover:border-primary dark:border-zinc-800',
                                )}
                            >
                                <div className="flex h-20 w-full items-end justify-center">
                                    <div
                                        className={cn('w-7 rounded-t', m.available ? 'bg-primary' : 'bg-gray-300 dark:bg-zinc-600')}
                                        style={{ height: `${Math.max(4, (Math.abs(m.earned) / maxEarned) * 80)}px` }}
                                    />
                                </div>
                                <span className="mt-1 font-semibold">{fmtMonthShort(m.month)}</span>
                                <span className="font-bold tabular-nums text-gray-800 dark:text-zinc-200">{brl(m.earned)}</span>
                                <span className="text-[10px] text-gray-400">{t('v2.gluteleader_wallet.cumulative')}</span>
                                <span className="tabular-nums text-[10px] text-gray-500">{brl(m.cumulative)}</span>
                            </button>
                        ))}
                    </div>
                )}
            </section>

            <section className="rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 pt-3 dark:border-zinc-800">
                    <div className="flex gap-1">
                        {(['statement', 'detail'] as const).map((key) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setTab(key)}
                                className={cn(
                                    'border-b-2 px-3 py-2 text-xs font-semibold',
                                    tab === key
                                        ? 'border-primary text-gray-900 dark:text-zinc-50'
                                        : 'border-transparent text-gray-500 hover:text-gray-800',
                                )}
                            >
                                {t(`v2.gluteleader_wallet.tab_${key}`)}
                            </button>
                        ))}
                    </div>
                    {month && wallet.months.length > 0 && (
                        <div className="flex items-center gap-2 pb-2">
                            <select
                                value={month}
                                onChange={(e) => setMonth(e.target.value)}
                                className="rounded-lg border border-gray-200 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-900"
                            >
                                {[...wallet.months].reverse().map((m) => (
                                    <option key={m.month} value={m.month}>{fmtMonthLong(m.month)}</option>
                                ))}
                            </select>
                            {tab === 'statement' && (
                                <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-zinc-300">
                                    <input type="checkbox" checked={showAllMonths} onChange={(e) => setShowAllMonths(e.target.checked)} />
                                    {t('v2.gluteleader_wallet.show_all_months')}
                                </label>
                            )}
                            <Button type="button" size="sm" variant="outline" isLoading={downloading} onClick={() => void downloadStatement()}>
                                <PiFilePdfBold className="mr-1 size-4" aria-hidden /> {t('v2.gluteleader_wallet.download_statement')}
                            </Button>
                        </div>
                    )}
                </div>

                <div className="p-4">
                    {tab === 'statement' && (
                        visibleRows.length === 0 ? (
                            <p className="py-6 text-center text-xs text-gray-500">
                                {showAllMonths
                                    ? t('v2.gluteleader_wallet.statement_empty')
                                    : t('v2.gluteleader_wallet.statement_month_empty', { month: selectedLabel })}
                            </p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs">
                                    <thead className="text-[10px] uppercase tracking-wide text-gray-500">
                                        <tr>
                                            <th className="py-2 pr-3">{t('v2.gluteleader_wallet.col_date')}</th>
                                            <th className="py-2 pr-3">{t('v2.gluteleader_wallet.col_desc')}</th>
                                            <th className="py-2 pr-3 text-right">{t('v2.gluteleader_wallet.col_credit')}</th>
                                            <th className="py-2 pr-3 text-right">{t('v2.gluteleader_wallet.col_debit')}</th>
                                            <th className="py-2 pr-3 text-right">{t('v2.gluteleader_wallet.running_balance')}</th>
                                            <th className="py-2" />
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                                        {visibleRows.map((row) => (
                                            <tr
                                                key={row.key}
                                                className={cn(row.month && 'cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-800/50')}
                                                onClick={() => {
                                                    if (row.month) {
                                                        setMonth(row.month);
                                                        setTab('detail');
                                                    }
                                                }}
                                            >
                                                <td className="whitespace-nowrap py-2 pr-3 tabular-nums text-gray-500">{fmtDate(row.date)}</td>
                                                <td className="py-2 pr-3">
                                                    <span className="flex items-center gap-2">
                                                        <span className={cn(
                                                            'flex size-6 shrink-0 items-center justify-center rounded-full',
                                                            row.amount >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-violet-100 text-violet-700',
                                                        )}
                                                        >
                                                            {row.amount >= 0 ? <PiArrowDownLeftBold className="size-3.5" /> : <PiArrowUpRightBold className="size-3.5" />}
                                                        </span>
                                                        <span>
                                                            <span className="font-semibold text-gray-900 dark:text-zinc-100">{row.title}</span>
                                                            {row.detail && <span className="block text-[11px] text-gray-500">{row.detail}</span>}
                                                        </span>
                                                    </span>
                                                </td>
                                                <td className="py-2 pr-3 text-right font-semibold tabular-nums text-emerald-700">{row.amount > 0 ? brl(row.amount) : ''}</td>
                                                <td className="py-2 pr-3 text-right font-semibold tabular-nums text-violet-700 dark:text-violet-300">{row.amount < 0 ? brl(Math.abs(row.amount)) : ''}</td>
                                                <td className="py-2 pr-3 text-right font-bold tabular-nums">{brl(row.balance)}</td>
                                                <td className="py-2 text-right">
                                                    <div className="flex items-center justify-end gap-1">
                                                    {row.month && (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                void downloadStatement(row.month ?? null);
                                                            }}
                                                            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-zinc-800"
                                                            title={t('v2.gluteleader_wallet.download_statement')}
                                                            aria-label={t('v2.gluteleader_wallet.download_statement')}
                                                        >
                                                            <PiFilePdfBold className="size-4" />
                                                        </button>
                                                    )}
                                                    {row.entry?.receipt_url && (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setReceiptPreview(row.entry?.receipt_url ?? null);
                                                            }}
                                                            className="overflow-hidden rounded border border-gray-200 dark:border-zinc-700"
                                                            title={t('v2.gluteleader_wallet.receipt')}
                                                        >
                                                            <img src={row.entry.receipt_url} alt="" className="size-8 object-cover" />
                                                        </button>
                                                    )}
                                                    {canManage && row.entry && (
                                                        <button
                                                            type="button"
                                                            aria-label={t('v2.gluteleader_wallet.delete_entry')}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setDeleteEntry(row.entry ?? null);
                                                            }}
                                                            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-rose-600 dark:hover:bg-zinc-800"
                                                        >
                                                            <PiTrashBold className="size-3.5" />
                                                        </button>
                                                    )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )
                    )}

                    {tab === 'detail' && month && (
                        monthQuery.isLoading || !monthQuery.data ? <LoadingState variant="table" /> : <GlMonthTable report={monthQuery.data} />
                    )}

                </div>
            </section>

            <V2Modal open={receiptPreview != null} onClose={() => setReceiptPreview(null)} title={t('v2.gluteleader_wallet.receipt')} size="lg">
                {receiptPreview && <img src={receiptPreview} alt="" className="mx-auto max-h-[70vh] rounded-lg object-contain" />}
            </V2Modal>

            {canManage && (
                <>
                    <EntryModal
                        open={modal === 'payout' || modal === 'adjustment'}
                        type={modal === 'adjustment' ? 'adjustment' : 'payout'}
                        beneficiaryId={beneficiary.id}
                        balance={wallet.balance}
                        onClose={() => setModal(null)}
                        onSaved={refresh}
                    />
                    <RateModal open={modal === 'rate'} beneficiary={beneficiary} onClose={() => setModal(null)} onSaved={refresh} />
                    <ConfigModal open={modal === 'config'} beneficiary={beneficiary} onClose={() => setModal(null)} onSaved={refresh} />
                    <ConfirmModal
                        open={deleteEntry != null}
                        onClose={() => setDeleteEntry(null)}
                        onConfirm={() => deleteEntry && removeEntry.mutate(deleteEntry.id)}
                        title={t('v2.gluteleader_wallet.delete_entry')}
                        description={t('v2.gluteleader_wallet.delete_confirm')}
                        confirmLabel={t('v2.gluteleader_wallet.delete_entry')}
                        loading={removeEntry.isPending}
                    />
                </>
            )}
        </div>
    );
}

const inputClass = 'mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900';

function EntryModal({
    open,
    type,
    beneficiaryId,
    balance,
    onClose,
    onSaved,
}: {
    open: boolean;
    type: 'payout' | 'adjustment';
    beneficiaryId: number;
    balance: number;
    onClose: () => void;
    onSaved: () => void;
}) {
    const { t } = useI18n();
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [method, setMethod] = useState('pix');
    const [receipt, setReceipt] = useState<File | null>(null);
    const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
    const [notes, setNotes] = useState('');

    useEffect(() => {
        if (open) {
            setAmount(type === 'payout' && balance > 0 ? String(balance) : '');
            setReceipt(null);
            setNotes('');
        }
    }, [open, type, balance]);

    useEffect(() => {
        if (!receipt) {
            setReceiptUrl(null);
            return undefined;
        }
        const url = URL.createObjectURL(receipt);
        setReceiptUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [receipt]);

    const save = useMutation({
        mutationFn: () => {
            const form = new FormData();
            form.append('type', type);
            form.append('amount', String(Number(amount.replace(',', '.'))));
            form.append('occurred_on', date);
            if (type === 'payout') form.append('method', method);
            if (notes) form.append('notes', notes);
            if (receipt) form.append('receipt', receipt);

            return api.post(`/commissioning/gluteleader/beneficiaries/${beneficiaryId}/entries`, form);
        },
        onSuccess: () => {
            toast.success(t('v2.gluteleader_wallet.saved'));
            onSaved();
            onClose();
        },
        onError: (err) => toast.error(apiMessage(err) ?? t('v2.gluteleader_wallet.amount_positive')),
    });

    return (
        <V2Modal
            open={open}
            onClose={onClose}
            title={type === 'payout' ? t('v2.gluteleader_wallet.register_payout') : t('v2.gluteleader_wallet.register_adjustment')}
            description={type === 'payout'
                ? `${t('v2.gluteleader_wallet.balance')}: ${brl(balance)}`
                : t('v2.gluteleader_wallet.adjustment_hint')}
            size="md"
            footer={(
                <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={onClose}>{t('v2.gluteleader_wallet.cancel')}</Button>
                    <Button
                        type="button"
                        isLoading={save.isPending}
                        disabled={!amount || (type === 'adjustment' && notes.trim().length < 3)}
                        onClick={() => save.mutate()}
                    >
                        {t('v2.gluteleader_wallet.save')}
                    </Button>
                </div>
            )}
        >
            <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-xs font-semibold text-gray-600">
                    {t('v2.gluteleader_wallet.amount_label')}
                    <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputClass} />
                </label>
                <label className="block text-xs font-semibold text-gray-600">
                    {t('v2.gluteleader_wallet.occurred_on')}
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputClass} />
                </label>
                {type === 'payout' && (
                    <>
                        <label className="block text-xs font-semibold text-gray-600">
                            {t('v2.gluteleader_wallet.method')}
                            <select value={method} onChange={(e) => setMethod(e.target.value)} className={inputClass}>
                                <option value="pix">{t('v2.gluteleader_wallet.method_pix')}</option>
                                <option value="ted">{t('v2.gluteleader_wallet.method_ted')}</option>
                                <option value="other">{t('v2.gluteleader_wallet.method_other')}</option>
                            </select>
                        </label>
                    </>
                )}
                {type === 'payout' && (
                    <div className="sm:col-span-2">
                        <p className="text-xs font-semibold text-gray-600">{t('v2.gluteleader_wallet.receipt_optional')}</p>
                        {receiptUrl ? (
                            <div className="mt-1 flex items-start gap-3 rounded-lg border border-gray-200 p-2 dark:border-zinc-700">
                                <img src={receiptUrl} alt="" className="max-h-40 rounded object-contain" />
                                <div className="min-w-0 flex-1 text-xs">
                                    <p className="truncate font-semibold">{receipt?.name}</p>
                                    <button type="button" onClick={() => setReceipt(null)} className="mt-1 text-rose-600 hover:underline">
                                        {t('v2.gluteleader_wallet.receipt_remove')}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <label className="mt-1 flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-200 px-3 py-5 text-xs text-gray-500 hover:border-primary dark:border-zinc-700">
                                <PiImageBold className="size-6 text-gray-400" aria-hidden />
                                {t('v2.gluteleader_wallet.receipt_pick')}
                                <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    className="hidden"
                                    onChange={(e) => setReceipt(e.target.files?.[0] ?? null)}
                                />
                            </label>
                        )}
                    </div>
                )}
                <label className="block text-xs font-semibold text-gray-600 sm:col-span-2">
                    {t('v2.gluteleader_wallet.notes')}
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputClass} />
                </label>
            </div>
        </V2Modal>
    );
}

function RateModal({ open, beneficiary, onClose, onSaved }: { open: boolean; beneficiary: Beneficiary; onClose: () => void; onSaved: () => void }) {
    const { t } = useI18n();
    const [ratePct, setRatePct] = useState('');
    const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
    const [notes, setNotes] = useState('');
    const [preview, setPreview] = useState<RatePreview | null>(null);

    useEffect(() => {
        if (open) {
            setRatePct(String(beneficiary.current_rate_pct));
            setPreview(null);
            setNotes('');
        }
    }, [open, beneficiary.current_rate_pct]);

    const call = useMutation({
        mutationFn: (apply: boolean) => api.post(`/commissioning/gluteleader/beneficiaries/${beneficiary.id}/rate`, {
            rate_pct: Number(ratePct.replace(',', '.')),
            effective_from: from,
            notes: notes || null,
            apply,
        }).then((r) => r.data as { data: RatePreview; applied: boolean }),
        onSuccess: (res) => {
            if (res.applied) {
                toast.success(t('v2.gluteleader_wallet.rate_applied'));
                onSaved();
                onClose();
            } else {
                setPreview(res.data);
            }
        },
        onError: (err) => toast.error(apiMessage(err) ?? t('v2.gluteleader_wallet.rate_invalid')),
    });

    return (
        <V2Modal
            open={open}
            onClose={onClose}
            title={t('v2.gluteleader_wallet.change_rate')}
            description={t('v2.gluteleader_wallet.retro_hint')}
            size="md"
            footer={(
                <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={onClose}>{t('v2.gluteleader_wallet.cancel')}</Button>
                    <Button type="button" variant="outline" isLoading={call.isPending && !preview} onClick={() => call.mutate(false)}>
                        {t('v2.gluteleader_wallet.preview')}
                    </Button>
                    <Button type="button" disabled={!preview} isLoading={call.isPending && !!preview} onClick={() => call.mutate(true)}>
                        {t('v2.gluteleader_wallet.apply_rate')}
                    </Button>
                </div>
            )}
        >
            <div className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                    <label className="block text-xs font-semibold text-gray-600">
                        {t('v2.gluteleader_wallet.rate_pct')}
                        <input inputMode="decimal" value={ratePct} onChange={(e) => { setRatePct(e.target.value); setPreview(null); }} className={inputClass} />
                    </label>
                    <label className="block text-xs font-semibold text-gray-600">
                        {t('v2.gluteleader_wallet.effective_from')}
                        <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPreview(null); }} className={inputClass} />
                    </label>
                </div>
                <label className="block text-xs font-semibold text-gray-600">
                    {t('v2.gluteleader_wallet.notes')}
                    <input value={notes} onChange={(e) => setNotes(e.target.value)} className={inputClass} />
                </label>

                {preview && (
                    <div className="grid gap-2 rounded-lg bg-gray-50 p-3 text-xs dark:bg-zinc-800/60 sm:grid-cols-3">
                        <div>
                            <p className="text-[10px] font-semibold uppercase text-gray-500">{t('v2.gluteleader_wallet.affected_deals')}</p>
                            <p className="text-sm font-bold">{preview.affected_deals}</p>
                        </div>
                        <div>
                            <p className="text-[10px] font-semibold uppercase text-gray-500">{t('v2.gluteleader_wallet.earned_delta')}</p>
                            <p className="text-sm font-bold tabular-nums">{brl(preview.earned_delta)}</p>
                        </div>
                        <div>
                            <p className="text-[10px] font-semibold uppercase text-gray-500">{t('v2.gluteleader_wallet.total_delta')}</p>
                            <p className="text-sm font-bold tabular-nums">{brl(preview.total_delta)}</p>
                        </div>
                    </div>
                )}

                <div>
                    <p className="text-[10px] font-semibold uppercase text-gray-500">{t('v2.gluteleader_wallet.rate_history')}</p>
                    <ul className="mt-1 space-y-0.5 text-xs text-gray-600 dark:text-zinc-300">
                        {beneficiary.rates.map((r) => (
                            <li key={r.effective_from}>
                                {r.rate_pct}% · {t('v2.gluteleader_wallet.since')} {fmtDate(r.effective_from)}
                                {r.notes ? ` · ${r.notes}` : ''}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </V2Modal>
    );
}

function ConfigModal({ open, beneficiary, onClose, onSaved }: { open: boolean; beneficiary: Beneficiary; onClose: () => void; onSaved: () => void }) {
    const { t } = useI18n();
    const [name, setName] = useState(beneficiary.name);
    const [email, setEmail] = useState(beneficiary.email ?? '');
    const [linkUser, setLinkUser] = useState(!!beneficiary.user);
    const [active, setActive] = useState(beneficiary.active);
    const [payoutNotes, setPayoutNotes] = useState(beneficiary.payout_notes ?? '');

    useEffect(() => {
        if (open) {
            setName(beneficiary.name);
            setEmail(beneficiary.email ?? '');
            setLinkUser(!!beneficiary.user);
            setActive(beneficiary.active);
            setPayoutNotes(beneficiary.payout_notes ?? '');
        }
    }, [open, beneficiary]);

    const save = useMutation({
        mutationFn: () => api.patch(`/commissioning/gluteleader/beneficiaries/${beneficiary.id}`, {
            name,
            email: email || null,
            link_user: linkUser,
            active,
            payout_notes: payoutNotes || null,
        }),
        onSuccess: () => {
            toast.success(t('v2.gluteleader_wallet.saved'));
            onSaved();
            onClose();
        },
        onError: (err) => toast.error(apiMessage(err) ?? t('v2.gluteleader_wallet.user_not_found')),
    });

    return (
        <V2Modal
            open={open}
            onClose={onClose}
            title={t('v2.gluteleader_wallet.configure')}
            size="md"
            footer={(
                <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={onClose}>{t('v2.gluteleader_wallet.cancel')}</Button>
                    <Button type="button" isLoading={save.isPending} onClick={() => save.mutate()}>{t('v2.gluteleader_wallet.save')}</Button>
                </div>
            )}
        >
            <div className="space-y-3">
                <label className="block text-xs font-semibold text-gray-600">
                    {t('v2.gluteleader_wallet.name')}
                    <input value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
                </label>
                <label className="block text-xs font-semibold text-gray-600">
                    {t('v2.gluteleader_wallet.email')}
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
                </label>
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-600">
                    <input type="checkbox" checked={linkUser} onChange={(e) => setLinkUser(e.target.checked)} />
                    {t('v2.gluteleader_wallet.link_user')}
                </label>
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-600">
                    <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                    {t('v2.gluteleader_wallet.active')}
                </label>
                <label className="block text-xs font-semibold text-gray-600">
                    {t('v2.gluteleader_wallet.payout_notes')}
                    <textarea value={payoutNotes} onChange={(e) => setPayoutNotes(e.target.value)} rows={3} className={inputClass} />
                </label>
            </div>
        </V2Modal>
    );
}

/** Mesma leitura da tela do vendedor: linhas do mês + subtotais (retroativo, bruto, inadimplência, perdidas, estorno, líquido). */
function GlMonthTable({ report }: { report: MonthReport }) {
    const { t } = useI18n();
    const rows: EarningRow[] = [
        ...report.items,
        ...report.defaulted_items.map((r) => ({ ...r, category: 'defaulted' as const })),
        ...report.forfeited_items.map((r) => ({ ...r, category: 'forfeited' as const })),
        ...report.clawback_items.map((r) => ({ ...r, category: 'clawback' as const })),
    ];

    if (rows.length === 0) {
        return <p className="py-6 text-center text-sm text-gray-400">{t('v2.gluteleader_wallet.month_empty_all')}</p>;
    }

    const rowTone = (row: EarningRow) => {
        switch (row.category) {
            case 'defaulted':
                return 'bg-red-50/70 dark:bg-red-950/20';
            case 'forfeited':
                return 'bg-violet-50/70 text-violet-900 dark:bg-violet-950/20 dark:text-violet-200';
            case 'clawback':
                return 'bg-amber-50/70 dark:bg-amber-950/20';
            default:
                return '';
        }
    };

    const amountCell = (row: EarningRow) => {
        if (row.category === 'defaulted') return <span className="text-red-600">−{brl(row.amount)}</span>;
        if (row.category === 'forfeited') return <span className="text-violet-700 line-through">{brl(row.amount)}</span>;
        if (row.category === 'clawback') return <span className="text-amber-700">−{brl(Math.abs(row.amount))}</span>;

        return brl(row.amount);
    };

    const totals: Array<{ label: string; hint?: string; count?: number; value: string; className: string; pill: string }> = [
        {
            label: t('v2.commissioning.history_month.subtotal_normal'),
            count: report.items.filter((r) => r.category !== 'retroactive').length,
            value: brl(report.normal_total),
            className: 'border-t border-gray-200 bg-gray-50 dark:border-zinc-600 dark:bg-zinc-800/95',
            pill: 'bg-gray-200 text-gray-800 dark:bg-zinc-700 dark:text-zinc-100',
        },
        {
            label: t('v2.commissioning.history_month.subtotal_retroactive'),
            count: report.items.filter((r) => r.category === 'retroactive').length,
            value: report.retroactive_total > 0 ? `+${brl(report.retroactive_total)}` : brl(0),
            className: 'bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100',
            pill: 'bg-amber-600 text-white',
        },
        {
            label: t('v2.commissioning.history_month.subtotal_gross'),
            hint: t('v2.commissioning.history_month.gross_hint'),
            value: brl(report.gross_total),
            className: 'bg-slate-100 text-slate-800 dark:bg-zinc-800/90 dark:text-zinc-100',
            pill: 'bg-slate-700 text-white dark:bg-zinc-600',
        },
        {
            label: t('v2.commissioning.history_month.subtotal_defaulted'),
            count: report.defaulted_items.length,
            value: `−${brl(report.defaulted_total)}`,
            className: 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200',
            pill: report.defaulted_items.length ? 'bg-red-600 text-white' : 'bg-red-400/70 text-white dark:bg-red-700/60',
        },
        {
            label: t('v2.commissioning.history_month.subtotal_forfeited'),
            count: report.forfeited_items.length,
            value: brl(report.forfeited_total),
            className: 'bg-violet-50 text-violet-800 dark:bg-violet-950/40 dark:text-violet-200',
            pill: 'bg-violet-700 text-white line-through',
        },
        {
            label: t('v2.gluteleader_wallet.subtotal_clawback'),
            count: report.clawback_items.length,
            value: `−${brl(report.clawback_total)}`,
            className: 'bg-orange-50 text-orange-900 dark:bg-orange-950/40 dark:text-orange-100',
            pill: report.clawback_items.length ? 'bg-orange-600 text-white' : 'bg-orange-300 text-white dark:bg-orange-800/60',
        },
        {
            label: t('v2.commissioning.history_month.net_total'),
            hint: t('v2.gluteleader_wallet.net_hint'),
            value: brl(report.net_total),
            className: 'border-t border-gray-300 bg-emerald-50/80 font-black dark:border-zinc-500 dark:bg-emerald-950/30',
            pill: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
        },
    ];

    return (
        <div className="space-y-3">
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-zinc-700">
                <table className="w-full text-left text-xs">
                    <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500 dark:bg-zinc-800">
                        <tr>
                            <th className="px-3 py-2.5">{t('v2.gluteleader_wallet.deal')}</th>
                            <th className="px-3 py-2.5">{t('v2.gluteleader_wallet.installment')}</th>
                            <th className="px-3 py-2.5">{t('v2.gluteleader_wallet.due')}</th>
                            <th className="px-3 py-2.5">{t('v2.gluteleader_wallet.paid')}</th>
                            <th className="px-3 py-2.5 text-right">{t('v2.gluteleader_wallet.base')}</th>
                            <th className="px-3 py-2.5 text-right">{t('v2.gluteleader_wallet.rate')}</th>
                            <th className="px-3 py-2.5 text-right">{t('v2.gluteleader_wallet.amount')}</th>
                            <th className="px-3 py-2.5 text-right">{t('v2.gluteleader_wallet.status')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                        {rows.map((row, idx) => (
                            <tr key={`${row.category}-${row.deal_id}-${row.installment_label}-${idx}`} className={rowTone(row)}>
                                <td className="max-w-[240px] px-3 py-2">
                                    <span className="inline-flex items-center rounded-full bg-gray-900 px-2.5 py-0.5 text-xs font-bold tabular-nums text-white dark:bg-zinc-100 dark:text-zinc-900">
                                        {row.deal_number ?? row.deal_id}
                                    </span>
                                    <span className="mt-0.5 block truncate text-gray-600 dark:text-zinc-300" title={row.deal_title ?? ''}>{row.deal_title}</span>
                                </td>
                                <td className="whitespace-nowrap px-3 py-2">
                                    {row.installment_label}
                                    {row.category === 'retroactive' && (
                                        <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-800">
                                            {t('v2.commissioning.history_month.badge_retroactive')}
                                        </span>
                                    )}
                                </td>
                                <td className="px-3 py-2 tabular-nums">{fmtDate(row.due_date)}</td>
                                <td className="px-3 py-2 tabular-nums">{fmtDate(row.paid_at)}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{brl(Math.abs(row.base))}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{row.rate_pct}%</td>
                                <td className="px-3 py-2 text-right font-semibold tabular-nums">{amountCell(row)}</td>
                                <td className="px-3 py-2 text-right">
                                    <Badge color={STATUS_COLOR[row.status]} variant="flat" rounded="pill" size="sm">
                                        {t(`v2.gluteleader_wallet.status_${row.status}`)}
                                    </Badge>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        {totals.map((row) => (
                            <tr key={row.label} className={cn('font-bold', row.className)}>
                                <td className="px-3 py-3" colSpan={6}>
                                    {row.label}
                                    {(row.hint || row.count !== undefined) && (
                                        <span className="ml-2 text-xs font-medium opacity-70">
                                            {row.hint}
                                            {row.hint && row.count !== undefined ? ' · ' : ''}
                                            {row.count !== undefined && t('v2.commissioning.history_month.rows_count', { count: row.count })}
                                        </span>
                                    )}
                                </td>
                                <td className="px-3 py-3 text-right" colSpan={2}>
                                    <span className={cn('inline-flex items-center rounded-full px-3 py-1 text-sm font-black tabular-nums', row.pill)}>
                                        {row.value}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tfoot>
                </table>
            </div>
            <div className="grid gap-2 text-[11px] text-gray-500 dark:text-zinc-400 md:grid-cols-2">
                <p className="rounded-lg bg-red-50/60 px-3 py-2 dark:bg-red-950/20">{t('v2.gluteleader_wallet.legend_defaulted')}</p>
                <p className="rounded-lg bg-orange-50/60 px-3 py-2 dark:bg-orange-950/20">{t('v2.gluteleader_wallet.legend_cancelled')}</p>
            </div>
        </div>
    );
}
